import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Plus, Repeat, Timer } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
// (voice dictation removed in iter15)

const DURATION_OPTIONS = [
  { value: "none", label: "No timer" },
  { value: "5", label: "5 min" },
  { value: "15", label: "15 min" },
  { value: "25", label: "25 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours" },
  { value: "120", label: "2 hours" },
];

export default function AddTaskForm({ onAdd, lists = [], defaultListId = null, defaultDurationMinutes = null }) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [listId, setListId] = useState(defaultListId || lists[0]?.id || "");
  const [recurrence, setRecurrence] = useState("none");
  const [dueDate, setDueDate] = useState(null);
  const [duration, setDuration] = useState(defaultDurationMinutes ? String(defaultDurationMinutes) : "none");
  const [open, setOpen] = useState(false);

  // Keep list_id in sync with the active filter / available lists
  useEffect(() => {
    if (defaultListId) {
      setListId(defaultListId);
    } else if (lists.length > 0 && !lists.find((l) => l.id === listId)) {
      setListId(lists[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultListId, lists]);

  useEffect(() => {
    setDuration(defaultDurationMinutes ? String(defaultDurationMinutes) : "none");
  }, [defaultDurationMinutes]);

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim() || !listId) return;
    onAdd({
      title: title.trim(),
      priority,
      list_id: listId,
      recurrence,
      due_date: dueDate ? dueDate.toISOString() : null,
      duration_minutes: duration && duration !== "none" ? parseInt(duration, 10) : null,
    });
    setTitle("");
    setDueDate(null);
    setRecurrence("none");
    setDuration("none");
  };

  return (
    <form
      onSubmit={submit}
      className="border border-zinc-200 bg-white"
      data-testid="add-task-form"
    >
      <div className="flex items-center border-b border-zinc-200">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a big task — e.g. Launch landing page"
          className="flex-1 px-5 py-4 text-base placeholder-zinc-400 outline-none bg-transparent min-w-0"
          data-testid="task-title-input"
        />
        <Button
          type="submit"
          disabled={!title.trim()}
          className="rounded-none h-[57px] px-4 sm:px-6 bg-zinc-900 hover:bg-[#002FA7] text-white text-sm font-medium uppercase tracking-[0.1em] disabled:opacity-30"
          data-testid="add-task-submit"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          <span className="hidden sm:inline">Add</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-100">
        <div className="bg-white">
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger
              className="rounded-none border-0 h-11 text-xs uppercase tracking-[0.1em] font-medium text-zinc-600"
              data-testid="priority-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="low" data-testid="priority-low">
                <span className="flex items-center gap-2"><span className="w-2 h-2 bg-blue-500" />Low</span>
              </SelectItem>
              <SelectItem value="medium" data-testid="priority-medium">
                <span className="flex items-center gap-2"><span className="w-2 h-2 bg-amber-500" />Medium</span>
              </SelectItem>
              <SelectItem value="high" data-testid="priority-high">
                <span className="flex items-center gap-2"><span className="w-2 h-2 bg-red-500" />High</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white">
          <Select value={listId} onValueChange={setListId}>
            <SelectTrigger
              className="rounded-none border-0 h-11 text-xs uppercase tracking-[0.1em] font-medium text-zinc-600"
              data-testid="list-select"
            >
              <SelectValue placeholder="List" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {lists.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`list-option-${l.id}`}>
                  <span className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 ${l.category === "work" ? "bg-zinc-900" : "bg-emerald-600"}`} />
                    {l.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full h-11 px-3 flex items-center gap-2 text-xs uppercase tracking-[0.1em] font-medium text-zinc-600 hover:bg-zinc-50"
                data-testid="due-date-picker"
              >
                <CalendarIcon className="w-4 h-4" strokeWidth={1.5} />
                <span className="truncate">{dueDate ? format(dueDate, "MMM d") : "Due date"}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none" align="start">
              <Calendar
                mode="single"
                selected={dueDate}
                onSelect={(d) => {
                  setDueDate(d);
                  setOpen(false);
                }}
                initialFocus
              />
              {dueDate && (
                <div className="p-2 border-t border-zinc-200">
                  <button
                    type="button"
                    onClick={() => setDueDate(null)}
                    className="text-xs text-zinc-500 hover:text-zinc-900"
                  >
                    Clear date
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        <div className="bg-white">
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger
              className="rounded-none border-0 h-11 text-xs uppercase tracking-[0.1em] font-medium text-zinc-600"
              data-testid="duration-select"
            >
              <span className="flex items-center gap-2 truncate">
                <Timer className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <SelectValue placeholder="Duration" />
              </span>
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {DURATION_OPTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value} data-testid={`duration-${d.value}`}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white">
          <Select value={recurrence} onValueChange={setRecurrence}>
            <SelectTrigger
              className="rounded-none border-0 h-11 text-xs uppercase tracking-[0.1em] font-medium text-zinc-600"
              data-testid="recurrence-select"
            >
              <span className="flex items-center gap-2 truncate">
                <Repeat className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="none">No repeat</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </form>
  );
}
